/**
 * Decimal rounding that matches Python's, for the numbers the client now computes twice (SD-3.5).
 *
 * ── WHY THIS EXISTS AT ALL ────────────────────────────────────────────────────────────────────
 * SD-3.5 recomputes production and savings on the client because NFR-2 forbids a network call for
 * a layout change. The backend computes the same figures with `Decimal` and stores them. Whenever
 * one number is produced by two languages, the rounding has to be the same in both or the surfaces
 * disagree — and SD-3.3 already paid for learning that the hard way, where a half-even/half-up
 * mismatch at the third decimal made `inputs_hash` stop colliding with no error anywhere.
 *
 * Binary rounding cannot do it. `(0.315).toFixed(2)` is "0.31", because the nearest double to
 * 0.315 is slightly below it, while `Decimal('0.315').quantize(Decimal('0.01'))` is 0.32. So the
 * rounding happens on the DECIMAL digits of the value's shortest round-tripping representation,
 * which is what `String(n)` gives in JS.
 *
 * ── TWO TIE RULES, AND THE DIFFERENCE IS THE BACKEND'S, NOT OURS ──────────────────────────────
 * `inputs_hash.py` quantizes with an EXPLICIT `rounding=ROUND_HALF_UP` (made explicit by SD-3.3,
 * after the mismatch). `derate.py:solar_access` and `financial.py` quantize with NO rounding
 * argument, which takes the decimal context default — ROUND_HALF_EVEN.
 *
 * So the codebase genuinely has both, and this module offers only the half-even one: the half-up
 * mirror already lives in `inputsHash.ts:decimalString` where the hash needs it, and giving the
 * hash a second implementation to choose from is how the two drift.
 *
 * ⚠️ CARRY (SD-3.5): that split is accidental on the Python side. One quantization is deliberate
 * and documented; the other is a context default nobody chose. Not changed here — Python is the
 * authority and this story mirrors it faithfully rather than moving it — but a backend story that
 * makes `derate.py` and `financial.py` state their rounding explicitly would remove a real trap.
 */

/**
 * Round to `scale` decimal places, ties to even, on the decimal digits.
 *
 * Mirrors `Decimal(str(v)).quantize(Decimal('0.0…1'))` under the default context.
 */
export function roundHalfEven(value: number, scale: number): number {
  if (!Number.isFinite(value)) return value;
  if (!Number.isInteger(scale) || scale < 0) {
    throw new Error(`roundHalfEven: scale must be a non-negative integer, got ${scale}`);
  }

  let text = String(value);
  // Exponent notation would be rounded literally ("5e-4" has no fractional digits to inspect).
  // Expanding well past the target scale is enough; the digits beyond it only decide the tie.
  if (/e/i.test(text)) text = value.toFixed(Math.max(scale + 4, 20));

  const negative = text.startsWith('-');
  if (negative || text.startsWith('+')) text = text.slice(1);

  const [intText = '0', fracText = ''] = text.split('.');
  if (fracText.length <= scale) return value;

  const kept = fracText.slice(0, scale);
  const dropped = fracText.slice(scale);

  let digits = BigInt(intText + kept);
  const isExactTie = dropped[0] === '5' && /^0*$/.test(dropped.slice(1));
  if (isExactTie) {
    if (digits % 2n === 1n) digits += 1n;
  } else if (Number(dropped[0]) >= 5) {
    digits += 1n;
  }

  const magnitude = Number(digits) / 10 ** scale;
  // -0 is a real double and renders as "-0"; a rounded-to-zero negative is just zero.
  return negative && magnitude !== 0 ? -magnitude : magnitude;
}
